import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';

import { useCurrentAddressInfo } from '@/hooks';

import {
  formatOfferPrice,
  parseSystemMessageData,
  getExpiredDaysCount,
  formatOfferPriceNumber,
} from '../../../utils';
import { HiMessageLocal, SystemMessageDataMaybeWeired } from '../../../types';
import styles from './index.module.css';

export default function SystemMessageItem({
  message,
  otherSideAccountName,
}: {
  message: HiMessageLocal;
  otherSideAccountName: string;
}) {
  const { t } = useTranslation();

  const { address: currentUserId } = useCurrentAddressInfo();

  const { offer, offerFromMe, price } = useMemo(() => {
    const { offer, price } = parseSystemMessageData(message.data);
    return {
      price,
      offer,
      offerFromMe: offer?.from_user_id === currentUserId,
    };
  }, [message.data, currentUserId]);
  const isBonusReceived = useMemo(
    () => message.attributes.is_bonus_received,
    [message.attributes.is_bonus_received],
  );
  const expiredDays = useMemo(
    () => getExpiredDaysCount(offer?.create_at, offer?.expire_at),
    [offer?.create_at, offer?.expire_at],
  );

  const boostedToPrice = useMemo(() => {
    return formatOfferPriceNumber((offer?.price || 0) + price, '$');
  }, [offer?.price, price]);

  return (
    <div className={styles.messageItemWrapper}>
      <div className={styles.inner}>
        <span className={styles.text}>
          {isBonusReceived ? (
            <Trans
              i18nKey="SystemMessageItem.isBonusReceived"
              values={{
                amount: formatOfferPriceNumber(
                  (message?.data as SystemMessageDataMaybeWeired)?.bonus || 0,
                  '$',
                ),
              }}>
              {' '}
              💰 You have received a <span className={styles.offerBonus}>$0</span> bonus from DeBank{' '}
            </Trans>
          ) : message.attributes.isOfferBoost ? (
            offerFromMe ? (
              <Trans i18nKey="SystemMessageItem.myOfferBoosted" values={{ price: boostedToPrice }}>
                Your Offer has been boosted to
                <span className={styles.offerStatus_charged_by_me_marked}>$0</span>
              </Trans>
            ) : (
              <Trans
                i18nKey="SystemMessageItem.receiveOfferBoosted"
                values={{ price: boostedToPrice }}>
                Offer boosted, read & earn
                <span className={styles.offerStatus_charged_by_me_marked}>$0</span>
              </Trans>
            )
          ) : (
            <>
              {!offerFromMe && offer?.status === 'charged' && (
                <Trans
                  i18nKey="SystemMessageItem.charged"
                  values={{ name: otherSideAccountName, amount: formatOfferPrice(offer, '$') }}>
                  Earned
                  {otherSideAccountName}'s{' '}
                  <span className={styles.offerStatus_charged_by_me_marked}>$0</span> offer
                </Trans>
              )}
              {offerFromMe && offer?.status === 'expired' && (
                <Trans
                  i18nKey="SystemMessageItem.expired"
                  values={{ amount: formatOfferPrice(offer, '$') }}>
                  Your <span className={styles.offerStatus_expired_marked}>$0</span> offer has been
                  returned
                </Trans>
              )}
              {offerFromMe && offer?.status === 'charged' && (
                <Trans
                  i18nKey="SystemMessageItem.meCharged"
                  values={{ name: otherSideAccountName, amount: formatOfferPrice(offer, '$') }}>
                  {otherSideAccountName}
                  accepted your{' '}
                  <span className={styles.offerStatus_charged_by_other_marked}>$0</span> offer
                </Trans>
              )}
              {!offerFromMe && offer?.status === 'expired' && (
                <>
                  <Trans
                    i18nKey="SystemMessageItem.otherExpired"
                    values={{ name: otherSideAccountName, amount: formatOfferPrice(offer, '$') }}>
                    {otherSideAccountName}'s
                    <span className={styles.offerStatus_expired_marked}>$0</span> offer has been
                    returned
                  </Trans>
                  <br />
                  {t('SystemMessageItem.expiredDays', { days: expiredDays })}
                </>
              )}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
